import { Resolver, Mutation, Arg, Ctx, Directive } from 'type-graphql'
import { In } from 'typeorm'

import { ScalarObject } from '@things-factory/shell'

import { UserPreference } from './user-preference'
import { NewUserPreference, UserPreferencePatch } from './user-preference-type'

@Resolver(UserPreference)
export class UserPreferenceMutation {
  @Directive('@transaction')
  @Mutation(returns => UserPreference, { description: 'To create new UserPreference' })
  async createUserPreference(
    @Arg('preference') preference: NewUserPreference,
    @Ctx() context: ResolverContext
  ): Promise<UserPreference> {
    const { domain, user, tx } = context.state

    return await tx.getRepository(UserPreference).save({
      ...preference,
      domain,
      creator: user,
      updater: user
    })
  }

  @Directive('@transaction')
  @Mutation(returns => UserPreference, { description: 'To modify UserPreference information' })
  async updateUserPreference(
    @Arg('id') id: string,
    @Arg('patch') patch: UserPreferencePatch,
    @Ctx() context: ResolverContext
  ): Promise<UserPreference> {
    const { domain, user, tx } = context.state

    const repository = tx.getRepository(UserPreference)
    const preference = await repository.findOne({
      where: { domain: { id: domain.id }, id }
    })

    return await repository.save({
      ...preference,
      ...patch,
      updater: user
    })
  }

  @Directive('@transaction')
  @Mutation(returns => UserPreference, { description: 'To create or update my preference' })
  async updateMyUserPreference(
    @Arg('key') key: string,
    @Arg('preference', type => ScalarObject) preference: { [key: string]: any },
    @Ctx() context: any
  ): Promise<UserPreference> {
    const { domain, user, tx } = context.state

    if (!key) {
      throw 'key is required.'
    }

    const myUserPreference = await tx.getRepository(UserPreference).findOne({
      where: { domain: { id: domain.id }, user: { id: user.id }, key }
    })

    return await tx.getRepository(UserPreference).save({
      ...myUserPreference,
      domain,
      user,
      key,
      preference,
      creator: user,
      updater: user
    })
  }

  @Directive('@transaction')
  @Mutation(returns => Boolean, { description: 'To delete my preference' })
  async deleteMyUserPreference(
    @Arg('key') key: string,
    @Arg('element') element: string,
    @Ctx() context: ResolverContext
  ): Promise<boolean> {
    const { domain, user, tx } = context.state

    await tx.getRepository(UserPreference).delete({
      domain: { id: domain.id },
      user: { id: user.id },
      key
    })

    return true
  }

  @Directive('@transaction')
  @Mutation(returns => Boolean, { description: 'To delete UserPreference' })
  async deleteUserPreference(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<boolean> {
    const { domain, tx } = context.state

    await tx.getRepository(UserPreference).delete({ domain: { id: domain.id }, id })

    return true
  }

  @Directive('@transaction')
  @Mutation(returns => Boolean, { description: 'To delete multiple UserPreferences' })
  async deleteUserPreferences(
    @Arg('ids', type => [String]) ids: string[],
    @Ctx() context: ResolverContext
  ): Promise<boolean> {
    const { domain, tx } = context.state

    await tx.getRepository(UserPreference).delete({
      domain: { id: domain.id },
      id: In(ids)
    })

    return true
  }
}
